// Track navigation history to manage complex back navigation flows
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Global store for navigation history
let navigationHistory: string[] = [];

// Source path tracking (where user came from before current route)
let sourcePathMap: Record<string, string> = {};

export const useNavigationHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  
  // Add current path to history on component mount/path change
  useEffect(() => {
    // Prevent duplicates
    if (navigationHistory.length === 0 || 
        navigationHistory[navigationHistory.length - 1] !== currentPath) {
      navigationHistory.push(currentPath);
      
      // If we have at least two items, record source path
      if (navigationHistory.length >= 2) {
        const previousPath = navigationHistory[navigationHistory.length - 2];
        sourcePathMap[currentPath] = previousPath;
        
        // Log navigation in development mode for debugging
        if (process.env.NODE_ENV === 'development') {
          console.log(
            `[Navigation History] Added path: ${currentPath}, Source: ${previousPath}`,
            '\nCurrent history:', [...navigationHistory],
            '\nSource map:', {...sourcePathMap}
          );
        }
      }
    }
  }, [currentPath]);

  // Navigate back to recorded source path
  const navigateBackToSource = useCallback((defaultPath: string = '/mobile-dashboard') => {
    if (sourcePathMap[currentPath]) {
      const sourcePath = sourcePathMap[currentPath];
      
      // Log in development mode
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Navigation History] Navigating back from ${currentPath} to source: ${sourcePath}`);
      }
      
      navigate(sourcePath);
      return true;
    } else {
      // Log in development mode
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Navigation History] No source path for ${currentPath}, using default: ${defaultPath}`);
      }
      
      navigate(defaultPath);
      return false;
    }
  }, [currentPath, navigate]);

  // Records a source path explicitly
  const setSourcePath = useCallback((sourcePath: string) => {
    sourcePathMap[currentPath] = sourcePath;
  }, [currentPath]);

  // Get the recorded source path for current location
  const getSourcePath = useCallback(() => {
    return sourcePathMap[currentPath] || null;
  }, [currentPath]);

  // Clear history
  const clearHistory = useCallback(() => {
    navigationHistory = [];
    sourcePathMap = {};
  }, []);

  return {
    navigateBackToSource,
    setSourcePath,
    getSourcePath,
    clearHistory
  };
};
